/**
 * computeDiscrepancyPerms — pure helper for resolving the per-event
 * discrepancy permission flags (Add / Edit / Change Status / View) from
 * the raw bootstrap state shape.
 *
 * Why this exists as a pure helper: the inline `useSelector` in
 * `CheckInScreen.js` was guessing field names (`discrepancyCreate` etc.)
 * that don't appear in the live 41-item permission catalog. The reviewer
 * pass on Rem21 Sub 1 caught the mismatch — correct names per
 * `calendar.event-discrepancies.js:160, 277, 317` are:
 *   - `discrepanciesAdd`     → canCreate
 *   - `discrepanciesManage`  → canEdit
 *   - `discrepanciesStatus`  → canChangeStatus
 *   - `discrepanciesView`    → canView
 *
 * Extracted so the selector logic is unit-testable per the project's
 * pure-utility test scope policy (`feedback_test_scope.md`).
 *
 * Bootstrap raw shape:
 *   `state.calendarMonthBootstrap.accessGroups` is the 41-item array of
 *   permission name strings for the CURRENT org (verified Rem20 Sub 1).
 *   `state.calendarMonthBootstrap.eventOrgPerms[orgUnique]` is a per-org
 *   override (used for cross-org events).
 *
 * @param {Object} bootstrap                Redux `state.calendarMonthBootstrap`.
 * @param {string|null} eventOrgUnique      The event's organizationUnique (overrides
 *                                          current-org if a cross-org event).
 * @returns {{canCreate: boolean, canEdit: boolean, canChangeStatus: boolean, canView: boolean}}
 */
import { normalizeAccessGroups } from './normalizeAccessGroups.js';

export function computeDiscrepancyPerms(bootstrap, eventOrgUnique) {
	const bs = bootstrap || {};
	const currentOrgUnique = bs.organization?.unique || bs.orgUnique || null;
	const map = { ...(bs.eventOrgPerms || {}) };
	if (currentOrgUnique && bs.accessGroups) map[currentOrgUnique] = bs.accessGroups;
	const orgUq = eventOrgUnique || currentOrgUnique || null;
	const ag = orgUq ? map[orgUq] : null;
	const rawShape = ag && typeof ag === 'object' && !Array.isArray(ag) && ag.accessGroups
		? ag.accessGroups
		: ag;
	const raw = normalizeAccessGroups(rawShape);
	return {
		canCreate: !!raw?.discrepanciesAdd,
		canEdit: !!raw?.discrepanciesManage,
		canChangeStatus: !!raw?.discrepanciesStatus,
		canView: !!raw?.discrepanciesView
	};
}
